import {useCallback, useEffect, useState} from 'react';
import {PixelRatio, Platform} from 'react-native';
import {get} from 'lodash';
import {isNil} from 'lodash/fp';

import {
  CANDIDATE_PROFILE,
  CLIENT_PROFILE,
  COACH_PROFILE,
} from '/enum/popover.enum';
import {REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE} from '/enum/records.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {shouldReleaseFeature} from '/util/featureRelease.util';

const MAX_NUMBER_OF_CLIENT = 5;
const ITEM_HEIGHT = 100;
const BUTTON_HEIGHT = 48;
const BAR_HEIGHT = 80;

// const ITEM_HEIGHT = 110;
// const BUTTON_HEIGHT = 60;
// const BAR_HEIGHT = 100;

export function usePeopleToContactSection({
  featureFlag,
  onOpenModal,
  onShowInfoPopover,
  onShowReport,
  clientsNumber,
}: any) {
  const fontScale = Math.min(Math.max(PixelRatio.getFontScale(), 1), 2);
  const scaledItemHeight = ITEM_HEIGHT * fontScale;
  const scaledButtonHeight = BUTTON_HEIGHT * fontScale;

  const {tt, ts} = useTranslation('screen.clients.section.peopleToContact');
  const [height, setHeight] = useState(
    Platform.OS === 'web' ? undefined : scaledItemHeight * 5.2 + BAR_HEIGHT,
  );

  const removeExpiringRewards = shouldReleaseFeature(
    get(featureFlag, 'ff_Remove_Expiring_Rewards', ''),
  );
  const updatePremierPlus = shouldReleaseFeature(
    get(featureFlag, 'ff_Premier_Plus', ''),
  );

  const onShowRecord = (arg: any) =>
    onOpenModal({modalName: COACH_PROFILE, modalProps: arg});

  const onShowProfile = ({id, reportName}: any) => {
    onOpenModal({
      modalName:
        reportName === REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE
          ? CANDIDATE_PROFILE
          : CLIENT_PROFILE,
      modalProps: {recordId: id, onShowRecord},
    });
  };

  const onInfoPress = (payload?: any) => {
    const infoBody = [
      {
        title: '',
        description:
          'People to contact provides updates from all Candidates and your Generation 0 Clients and Coaches.',
      },
      ...infoText.map(reportDetails => ({
        title: reportDetails.reportName,
        description: reportDetails.reportInfo,
      })),
    ];

    const popoverPayload = {
      heading: tt('info.heading', 'People to Contact'),
      body: infoBody,
      ...payload,
    };

    onShowInfoPopover?.(popoverPayload);
  };

  const infoText = [
    {
      id: 1,
      reportName: ts('nextTask', 'Next task:'),
      reportInfo: ts(
        'nextTaskDesc',
        'Next task to complete on Client journey.',
      ),
    },
    {
      id: 2,
      reportName: ts(
        'premierCancellation',
        updatePremierPlus ? 'Premier+ cancellation:' : 'Premier cancelation:',
      ),
      reportInfo: ts(
        'premierDesc',
        updatePremierPlus
          ? 'Clients who have canceled their order within the last 30 days and have not signed up for Premier+ again.'
          : 'Clients who have canceled their order within the last 30 days and have not scheduled a new Premier order date.',
      ),
    },
    {
      id: 3,
      reportName: ts('noActive', 'No ACTIVE order:'),
      reportInfo: ts(
        'noActiveDesc',
        'Clients who have placed an order within the last 30 days that did not have an ACTIVE product in it.',
      ),
    },
    {
      id: 4,
      reportName: ts('noOrder', 'No order:'),
      reportInfo: ts(
        'noOrderDesc',
        'Clients who are within their first 90 days of signing up that have not placed an order.',
      ),
    },
    {
      id: 5,
      reportName: ts('recInactive', 'Recently inactive:'),
      reportInfo: ts(
        'recInactiveDesc',
        'Clients that have not ordered between 60 and 90 days.',
      ),
    },
    {
      id: 6,
      reportName: ts('expSoon', 'Expiring soon:'),
      reportInfo: ts(
        'expSoonDesc',
        !removeExpiringRewards
          ? 'Clients who have a Reward or wellness credit that is expiring within the next 7 days.'
          : 'Clients who have a wellness credit that is expiring within the next 7 days.',
      ),
    },
    {
      id: 7,
      reportName: ts('newCandidate', 'New Candidates:'),
      reportInfo: ts(
        'newCandidateDesc',
        'Candidates who have been added to your Candidate Report within the last 7 days.',
      ),
    },
  ];

  const onItemPress = (payload: any) => onShowReport(payload);

  const calculateHeight = useCallback(() => {
    // We count the height because the tabs in ScrollView only work with a fixed height
    const showingClientsNumber =
      clientsNumber >= MAX_NUMBER_OF_CLIENT && !isNil(clientsNumber)
        ? MAX_NUMBER_OF_CLIENT
        : clientsNumber;

    const buttonHeight =
      clientsNumber <= MAX_NUMBER_OF_CLIENT ? 0 : scaledButtonHeight;

    return showingClientsNumber * scaledItemHeight + BAR_HEIGHT + buttonHeight;
  }, [clientsNumber, scaledButtonHeight, scaledItemHeight]);

  useEffect(() => {
    if (!isNil(clientsNumber)) {
      // @ts-expect-error TS(2345): Argument of type '() => number' is not assignable ... Remove this comment to see the full error message
      setHeight(calculateHeight);
    }
  }, [calculateHeight, clientsNumber]);

  return {
    onShowProfile,
    onInfoPress,
    onItemPress,
    tt,
    ts,
    height,
  };
}
